import React from 'react';
import { Clock, Euro, Star, Plane, Train } from 'lucide-react';
import SEO from '../components/SEO';
import HeroMockup from '../components/HeroMockup';
import BookingSection from '../components/BookingSection';
import { PopularTripsGrid } from '../components/PopularTripsProof';
import BrandTrustBanner from '../components/BrandTrustBanner';
import AnswerFirstBlock from '../components/AnswerFirstBlock';
import { BrandFinalCTA } from '../components/BrandSections';
import Reveal from '../components/Reveal';

// Lazy-load below-the-fold sections for LCP/TBT optimization
const GoogleReviewsCarousel = React.lazy(() => import('../components/GoogleReviewsCarousel'));
const HomeFAQ = React.lazy(() => import('../components/HomeFAQ'));
const HomeSeoContent = React.lazy(() => import('../components/HomeSeoContent'));
const InternalLinksGrid = React.lazy(() => import('../components/InternalLinksGrid'));

const homeKeywords = [
  'vtc nantes', 'chauffeur privé nantes', 'taxi nantes', 'vtc aéroport nantes',
  'chauffeur nantes', 'vtc 44', 'vtc loire atlantique', 'taxi aéroport nantes',
  'transfert aéroport nantes', 'vtc gare nantes', 'chauffeur privé 44',
  'vtc saint nazaire', 'vtc la baule', 'réservation vtc nantes',
  'vtc pas cher nantes', 'alternative taxi nantes', 'vtc premium nantes'
].join(', ');

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://goloire-cab.fr/" }]
};

// NOTE: Product schema removed (2026-05-16) — was causing GSC "Extraits d'avis 2 invalides"
// because it had aggregateRating without embedded reviews. The TaxiService schema
// (defined in public/index.html) already carries aggregateRating + 5 embedded reviews,
// which is the canonical authority for the homepage's review snippets.

const Home = () => {
  const staticTitle = "VTC Nantes 24h/24 · Chauffeur Privé Aéroport & Gares";
  const staticDescription = "VTC Nantes ⭐ Chauffeur privé disponible 24h/24. Transfert aéroport Nantes Atlantique, navette vtc gare SNCF, longue distance. Réservation VTC ☎️ 02 59 22 20 00";

  return (
    <>
      <SEO
        canonical="https://goloire-cab.fr/"
        title={staticTitle}
        description={staticDescription}
        keywords={homeKeywords}
        schema={[breadcrumbSchema]}
      />

      <main>
        {/* Hero - chargement immediat (LCP) */}
        <HeroMockup />

        {/* Iframe de reservation directement sous le Hero pour conversion maximale */}
        <BookingSection />

        {/* Bandeau de réassurance (garanties, flotte, tarif fixe) */}
        <BrandTrustBanner />

        {/* Answer-first block (AEO / featured-snippet target) */}
        <AnswerFirstBlock
          testIdPrefix="home-answer-first"
          question="Qu'est-ce que Goloire-Cab et comment réserver un VTC à Nantes ?"
          answer={(
            <>
              <strong>Goloire-Cab</strong> est un service de VTC premium basé à Nantes (44),
              disponible <strong>24h/24, 7j/7</strong>, opéré par des chauffeurs privés
              professionnels avec une flotte 100 % électrique <strong>Tesla &amp; Kia EV6</strong>.
              Réservez en <strong>2 minutes</strong> par téléphone, sur le formulaire en ligne
              ou via WhatsApp : transfert aéroport Nantes Atlantique <strong>dès 40 €</strong>,
              gare SNCF dès 30 €, tarif fixe garanti sans supplément nuit ni week-end.
            </>
          )}
          keyFacts={[
            { icon: Clock, label: 'Disponible 24h/24 · 7j/7' },
            { icon: Euro, label: 'Tarif fixe garanti' },
            { icon: Plane, label: 'Aéroport Nantes dès 40 €' },
            { icon: Train, label: 'Gare SNCF dès 30 €' },
            { icon: Star, label: '5/5 · 159 avis Google' },
          ]}
        />

        {/* Avis Google — preuve sociale unique de la page */}
        <React.Suspense fallback={<div style={{ minHeight: '500px' }} aria-hidden="true" />}>
          <Reveal><GoogleReviewsCarousel /></Reveal>
        </React.Suspense>

        {/* Trajets les plus demandés — preuve d'activité + maillage interne */}
        <Reveal><PopularTripsGrid testIdPrefix="home-trips-grid" /></Reveal>

        {/* Contenu SEO + FAQ + liens internes */}
        <React.Suspense fallback={<div style={{ minHeight: '800px' }} aria-hidden="true" />}>
          <div className="cv-auto"><HomeSeoContent /></div>
          <div className="cv-auto"><HomeFAQ /></div>
          <BrandFinalCTA />
          <div className="cv-auto"><InternalLinksGrid variant="full" /></div>
        </React.Suspense>
      </main>
    </>
  );
};

export default Home;
